import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Table, Input, Dropdown, Tag, Spin } from "antd";
import {
  SearchOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { toast } from "react-hot-toast";
import DeleteConfirmationModal from "../../../../../Components/Common/DeleteConfirmationModal";
import Spinner from "../../../../../Components/Common/Spinner";
import {
  fetchAllReceipts,
  cancelReceipt,
} from "../../../../../Store/Slices/Finance/Receipts/receiptsThunks";
import ProtectedSection from "../../../../../Routes/ProtectedRoutes/ProtectedSection";
import { PERMISSIONS } from "../../../../../config/permission";
// Import jsPDF and html2canvas
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ProtectedAction from "../../../../../Routes/ProtectedRoutes/ProtectedAction";

// Your renamed Receipt component
import Receipt from "../../../../../Utils/FinanceTemplate/Receipt";
import { useTranslation } from "react-i18next";
import TableShimmer from "../../../../../Components/Common/TableShimmer";

const RecentReceipts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currency = useSelector(
    (store) => store.common.user.userDetails.currency,
  );
  const {
    receipts = [],
    loading,
    error,
    pagination = {},
  } = useSelector((state) => state.admin.receipts || {});
  const { t } = useTranslation("prtFinance");
  // const [searchQuery, setSearchQuery] = useState("");
  // const [dataFetched, setDataFetched] = useState(false);
  // const [fetching, setFetching] = useState(true);
  // // For canceling a receipt
  // const [modalVisible, setModalVisible] = useState(false);
  // const [selectedReceiptId, setSelectedReceiptId] = useState(null);
  // const [cancelLoading, setCancelLoading] = useState(false);

  // // For previewing a receipt in a custom modal
  // const [isReceiptVisible, setReceiptVisible] = useState(false);
  // const [selectedReceipt, setSelectedReceipt] = useState(null);

  // Detect outside clicks to close the popup
  const popupRef = useRef(null);

  useEffect(() => {
    dispatch(fetchAllReceipts({ page: 1, limit: 5, isCancel: false }));
  }, [dispatch]);

  // const handleDownloadPDF = async () => {
  //   if (!selectedReceipt) return;
  //   try {
  //     const pdfTitle = selectedReceipt.receiptNumber
  //       ? `${selectedReceipt.receiptNumber}.pdf`
  //       : "receipt.pdf";

  //     const canvas = await html2canvas(popupRef.current, { scale: 2 });
  //     const imgData = canvas.toDataURL("image/png");

  //     const pdf = new jsPDF("p", "pt", "a4");
  //     const pageWidth = pdf.internal.pageSize.getWidth();
  //     const pageHeight = pdf.internal.pageSize.getHeight();

  //     const imgWidth = canvas.width;
  //     const imgHeight = canvas.height;
  //     const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
  //     const newWidth = imgWidth * ratio;
  //     const newHeight = imgHeight * ratio;

  //     pdf.addImage(imgData, "PNG", 0, 0, newWidth, newHeight);
  //     pdf.save(pdfTitle);
  //   } catch (error) {
  //     console.error("Error generating PDF:", error);
  //     toast.error("Failed to generate PDF.");
  //   }
  // };

  const columns = [
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Reciept Number")}
        </span>
      ),
      dataIndex: "_id",
      render: (text) => (
        <span className="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-gray-100 text-gray-600 border border-gray-200">
          {text?.toUpperCase()}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Invoice Number")}
        </span>
      ),
      dataIndex: "invoiceNumber",
      render: (text) => (
        <span className="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-purple-50 text-purple-600 border border-purple-100">
          {text}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {`${t("Total Amount")} ${currency}`}
        </span>
      ),
      render: (_, record) => {
        let total = 0;
        record.paidItems.map((i) => {
          total += i.amountPaid;
        });
        return (
          <span className="text-sm font-semibold text-gray-700">
            {total.toFixed(2)}
          </span>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Payment Status")}
        </span>
      ),
      dataIndex: "paymentStatus",
      render: (status) => {
        const color =
          status === "paid" ? "green" : status === "Unpaid" ? "red" : "blue";
        const map = {
          green: "bg-green-50 text-green-600 border-green-200",
          red: "bg-red-50 text-red-500 border-red-100",
          blue: "bg-blue-50 text-blue-600 border-blue-100",
        };
        const dot = {
          green: "bg-green-500",
          red: "bg-red-400",
          blue: "bg-blue-500",
        };
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold capitalize tracking-wider border ${map[color] || "bg-gray-50 text-gray-500 border-gray-100"}`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot[color] || "bg-gray-400"}`}
            />
            {status}
          </span>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Payment Method")}
        </span>
      ),
      dataIndex: "paymentType",
      render: (value) => {
        const capitalizedStatus =
          value.charAt(0).toUpperCase() + value.slice(1);
        return (
          <span className="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-600 border border-indigo-100">
            {capitalizedStatus}
          </span>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {t("Date")}
        </span>
      ),
      dataIndex: "paymentDate",
      render: (paymentDate) => (
        <span className="text-sm text-gray-600">
          {`${paymentDate?.slice(0, 10)}`}
        </span>
      ),
    },
  ];

  return (
    <div className="rounded-xl  gap-y-4 mt-4">
      <div className=" flex justify-between items-center bg-white rounded-xl shadow-sm p-4 mb-4">
        <h2 className="staff-heading">{t("Recent Receipts List")}</h2>

        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <button
            onClick={() => navigate("/finance/receipts/receipt-list")}
            className="px-4 py-1 btn-gradient"
          >
            {t("View More")}
          </button>
        </div>
      </div>
      {loading ? (
        <TableShimmer columns={6} rows={6} />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <Table
            rowKey={(record) => record._id}
            columns={columns}
            dataSource={receipts}
            size="small"
            pagination={false}
            components={{
              body: {
                row: (props) => <tr {...props} style={{ height: "60px" }} />,
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default RecentReceipts;
